import { memo, useMemo, useState } from "react";
import ImageLightbox from "./ImageLightbox";
import { imageUrl, imageUrls } from "../lib/dataClient";
import {
  haversineDistanceM,
  walkMinutes,
  carMinutes,
  formatDistance,
  formatDuration,
  walkDurationText,
  carDurationText,
} from "../lib/distance";

/**
 * 카페 상세 (시트 안 콘텐츠)
 *
 * Above-the-fold (peek/half):
 *   [chip + 이름 + 동네/카테고리]
 *   [📍 내 위치 거리]
 *
 * Below-the-fold (full):
 *   [정보 카드: 주소, 전화]
 *   [사장님 추천 맛집 — 텍스트 위주 가로 스와이프 카드, 거리순]
 *   [네이버 지도에서 보기]
 */
export default memo(function CafeDetail({
  cafe,
  places,
  userLocation,
  onSelectPlace,
  selectedPlaceId,
}) {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // 추천 맛집 + 거리 → 거리순 정렬 (내 위치 우선)
  const recommendedPlaces = useMemo(() => {
    const list = (cafe.recommends || [])
      .map((rec) => {
        const place = places.find((p) => p.id === rec.placeId);
        if (!place || place.lat == null) return null;
        const distFromCafe = haversineDistanceM(
          cafe.lat,
          cafe.lng,
          place.lat,
          place.lng
        );
        const distFromUser = userLocation
          ? haversineDistanceM(
              userLocation.lat,
              userLocation.lng,
              place.lat,
              place.lng
            )
          : null;
        return {
          ...place,
          menuHighlight: rec.menuHighlight,
          note: rec.note,
          routing: rec.routing,  // 실제 도로 경로 데이터 (drivingDistance/Duration/Path)
          distFromCafe,
          distFromUser,
        };
      })
      .filter(Boolean);

    if (userLocation) {
      list.sort(
        (a, b) => (a.distFromUser ?? Infinity) - (b.distFromUser ?? Infinity)
      );
    } else {
      list.sort((a, b) => a.distFromCafe - b.distFromCafe);
    }
    return list;
  }, [cafe, places, userLocation]);

  const distFromUserToCafe = userLocation
    ? haversineDistanceM(
        userLocation.lat,
        userLocation.lng,
        cafe.lat,
        cafe.lng
      )
    : null;

  const category = cafe.naverCategory?.split(">").pop()?.trim();

  return (
    <div className="cf-cafedetail">
      {/* === Above-the-fold === */}

      <div className="cf-cafedetail__head">
        <div className="cf-cafedetail__chip">
          <span className="cf-cafedetail__chip-dot" />
          Cafe
        </div>
        <h2 className="cf-cafedetail__name">{cafe.name}</h2>
        {(cafe.neighborhood || category) && (
          <div className="cf-cafedetail__head-sub">
            {cafe.neighborhood && <span>{cafe.neighborhood}</span>}
            {cafe.neighborhood && category && (
              <span className="cf-cafedetail__head-dot">·</span>
            )}
            {category && <span>{category}</span>}
          </div>
        )}
      </div>

      {distFromUserToCafe != null && (
        <div className="cf-cafedetail__quick-dist">
          <span className="cf-cafedetail__quick-dist-icon">📍</span>
          <span className="cf-cafedetail__quick-dist-text">
            내 위치에서{" "}
            <strong>{formatDistance(distFromUserToCafe)}</strong>
            <span className="cf-cafedetail__quick-dist-sub">
              {" · "}🚶 {walkDurationText(distFromUserToCafe)} · 🚗{" "}
              {carDurationText(distFromUserToCafe)}
            </span>
          </span>
        </div>
      )}

      {Array.isArray(cafe.images) && cafe.images.length > 0 && (
        <div className="cf-cafedetail__thumbs">
          {cafe.images.map((src, idx) => (
            <div
              className="cf-cafedetail__thumb"
              key={idx}
              onClick={() => setLightboxIndex(idx)}
              role="button"
              tabIndex={0}
            >
              <img
                src={imageUrl(src)}
                alt={`${cafe.name} ${idx + 1}`}
                className="cf-cafedetail__thumb-img"
              />
            </div>
          ))}
        </div>
      )}

      {/* === Below-the-fold === */}

      <div className="cf-cafedetail__infobox">
        {cafe.roadAddress && (
          <div className="cf-cafedetail__inforow">
            <span className="cf-cafedetail__info-icon">📍</span>
            <span className="cf-cafedetail__info-text">{cafe.roadAddress}</span>
          </div>
        )}
        {cafe.naverTelephone && (
          <div className="cf-cafedetail__inforow">
            <span className="cf-cafedetail__info-icon">📞</span>
            <span className="cf-cafedetail__info-text">
              {cafe.naverTelephone}
            </span>
          </div>
        )}
      </div>

      <div className="cf-cafedetail__section">
        <div className="cf-cafedetail__section-head">
          <h3 className="cf-cafedetail__section-title">사장님 추천 맛집</h3>
          <span className="cf-cafedetail__section-meta">
            {userLocation ? "내 위치 기준 가까운 순" : "가까운 순"} ·{" "}
            {recommendedPlaces.length}곳
          </span>
        </div>

        {recommendedPlaces.length === 0 ? (
          <div className="cf-cafedetail__empty">
            아직 추천 맛집 정보가 준비되지 않았어요.
          </div>
        ) : (
          <div className="cf-cafedetail__places-swipe">
            {recommendedPlaces.map((p) => {
              const isSelected = p.id === selectedPlaceId;
              const placeCategory = p.naverCategory?.split(">").pop()?.trim();
              return (
                <button
                  key={p.id}
                  className={`cf-placecard-swipe ${
                    isSelected ? "is-selected" : ""
                  }`}
                  onClick={() => onSelectPlace?.(p)}
                >
                  <div className="cf-placecard-swipe__head">
                    <div className="cf-placecard-swipe__chip">
                      <span className="cf-placecard-swipe__chip-dot" />
                      Restaurant
                    </div>
                    <div className="cf-placecard-swipe__name">{p.name}</div>
                    {(placeCategory || p.menuHighlight) && (
                      <div className="cf-placecard-swipe__sub">
                        {placeCategory && <span>{placeCategory}</span>}
                        {placeCategory && p.menuHighlight && (
                          <span className="cf-placecard-swipe__sub-dot">·</span>
                        )}
                        {p.menuHighlight && (
                          <span className="cf-placecard-swipe__rec">
                            {p.menuHighlight}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {p.note && (
                    <div className="cf-placecard-swipe__note">
                      <span className="cf-placecard-swipe__note-icon">💬</span>
                      <span className="cf-placecard-swipe__note-text">
                        {p.note}
                      </span>
                    </div>
                  )}

                  <div className="cf-placecard-swipe__dists">
                    {p.routing ? (() => {
                      const walkSec = p.routing.walking?.duration ?? p.routing.walkingDurationEst ?? 0;
                      const walkingDist = p.routing.walking?.distance ?? p.routing.drivingDistance;
                      const isAdjacent = walkingDist < 200;
                      return (
                        <div className="cf-placecard-swipe__dist">
                          <span className="cf-placecard-swipe__dist-icon">☕</span>
                          {isAdjacent ? (
                            <strong>걸어서 {formatDuration(walkSec)}</strong>
                          ) : (
                            <strong>차 {formatDuration(p.routing.drivingDuration)}{" · "}도보 {formatDuration(walkSec)}</strong>
                          )}
                          <span className="cf-placecard-swipe__dist-sub">
                            {formatDistance(p.routing.drivingDistance)}
                          </span>
                        </div>
                      );
                    })() : (
                      <div className="cf-placecard-swipe__dist">
                        <span className="cf-placecard-swipe__dist-icon">☕</span>
                        <strong>도보 {walkDurationText(p.distFromCafe)}</strong>
                        <span className="cf-placecard-swipe__dist-sub">
                          {formatDistance(p.distFromCafe)}
                        </span>
                      </div>
                    )}
                    {p.distFromUser != null && (
                      <div className="cf-placecard-swipe__dist">
                        <span className="cf-placecard-swipe__dist-icon">📍</span>
                        <strong>도보 {walkDurationText(p.distFromUser)}</strong>
                        <span className="cf-placecard-swipe__dist-sub">
                          {formatDistance(p.distFromUser)}
                        </span>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {cafe.naverMapUrl && (
        <div className="cf-cafedetail__actions">
          <a
            className="cf-cafedetail__action-btn"
            href={cafe.naverMapUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            네이버 지도에서 보기 ↗
          </a>
        </div>
      )}

      {Array.isArray(cafe.images) && cafe.images.length > 0 && (
        <ImageLightbox
          images={imageUrls(cafe.images)}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
          alt={cafe.name}
        />
      )}
    </div>
  );
});
